import React from 'react';
import StudyItemCard from '../../../organisms/study_item_card';
import SecondaryCTA from '../secondary_actions';
import style from './style.module.scss';
import cx from 'classnames';
import StatusMessage from '@magoosh/gre/adaptive_prep_v2/lib/status_message';
import { Checkpoint } from '@magoosh/gre/organisms/checkpoints/types';
import { useQueries } from '@magoosh/gre/app/queries';
import ExpansionPanel, { ExpansionPanelGroup } from '@magoosh/gre/adaptive_prep_v2/lib/expansion_panel';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import RestDayMessage from '@magoosh/gre/adaptive_prep_v2/lib/rest_day_message';
import ContextualLink from '@magoosh/gre/app/contextual_link';
import CheckpointsTab from '../checkpoints_tab';

interface PresetPlanProps {
  allDayNumbers: number[];
  checkpoints: Checkpoint[];
  currentDayNumber: number;
  prioritizedCheckpoint: React.ReactNode;
  tab: 'checkpoints' | 'plan';
}

export const PresetPlan: React.FC<PresetPlanProps> = (props) => {
  const q = useQueries();

  const presetStudyPlanItemsQuery = q.presetStudyPlanItemsQuery();
  const updateStudyPlan = (params: { presetPlanCurrentDayNumber: number }) =>
    q.updateStudyPlanMutation.mutate(params);

  const { allDayNumbers, checkpoints, currentDayNumber, prioritizedCheckpoint, tab } = props;

  const isPlanTab = tab === 'plan';
  const isCheckpointsTab = tab === 'checkpoints';
  const showNext = currentDayNumber > 1;
  const showPrevious = currentDayNumber < allDayNumbers.length;

  let isLargerItemSet = false;
  const calculateIsLargerItemSet = (isLarger) => {
    if (isLarger) isLargerItemSet = true;
    return isLarger;
  };

  if (presetStudyPlanItemsQuery.isLoading) {
    return (
      <StatusMessage headline="How exciting!" text="Your study plan is almost ready..." isColored={true} />
    );
  } else if (isPlanTab) {
    const items = presetStudyPlanItemsQuery.data;
    return (
      <>
        {prioritizedCheckpoint}

        <div className="d-flex justify-content-between align-items-center">
          <h3>Day {currentDayNumber}</h3>
          <ExpansionPanelGroup>
            <ExpansionPanel text="All Days">
              {() =>
                allDayNumbers.map((day) => {
                  const prevDay = day - 1;
                  return (
                    <>
                      {prevDay % 7 === 0 && (
                        <h5 className={style.weekNumber}>
                          <strong>WEEK {(prevDay + 7) / 7}</strong>
                        </h5>
                      )}
                      {currentDayNumber === day ? (
                        <p
                          className={cx(
                            style.dayNumber,
                            'u-margin-B-n d-flex justify-content-between align-items-center'
                          )}>
                          Day {day}{' '}
                          <h3 className="u-margin-V-n">
                            <FontAwesomeIcon icon="check-circle" />
                          </h3>
                        </p>
                      ) : (
                        <ContextualLink
                          className={style.dayNumber}
                          key={day}
                          onClick={() => updateStudyPlan({ presetPlanCurrentDayNumber: day })}>
                          Day {day}
                        </ContextualLink>
                      )}
                    </>
                  );
                })
              }
            </ExpansionPanel>
          </ExpansionPanelGroup>
        </div>
        {items && items.length === 0 && (
          <div className="text-center u-margin-V-l">
            <FontAwesomeIcon icon="spinner" className="fa-pulse fa-3x" />
          </div>
        )}
        {items &&
          items.map((item, index) => {
            const isLarger = !item.completedAt && !isLargerItemSet && !prioritizedCheckpoint;
            return item.type !== 'Rest' ? (
              <StudyItemCard
                key={index}
                type={item.type}
                section={item.section}
                variant={item.type === 'Segment' ? 'secondary' : 'info'}
                headline={item.headline}
                subheadline={item.subheadline}
                significanceLevel={item.significanceLevel}
                minutes={item.estimatedTimeMinutes}
                id={item.id}
                primaryCTA={{
                  ...item.primaryCta,
                  variant: isLarger ? 'secondary' : 'default'
                }}
                secondaryCTA={<SecondaryCTA item={item} planType="preset" />}
                isLarger={calculateIsLargerItemSet(isLarger)}
                description={item.description}
                shortDescription={item.shortDescription}
                locked={item.locked}
                isCompleted={!!item.completedAt}
              />
            ) : (
              <div className="'u-margin-V-xl'">
                <RestDayMessage headline={item.headline} text={item.description} />
              </div>
            );
          })}
        <div className="d-flex justify-content-center">
          {showNext && (
            <ContextualLink
              className="u-margin-R-xs text-gray-dark text-no-decoration"
              onClick={() => updateStudyPlan({ presetPlanCurrentDayNumber: currentDayNumber - 1 })}>
              &#x2190; Previous
            </ContextualLink>
          )}
          {showNext && showPrevious && <div className={style.verticalLine}></div>}
          {showPrevious && (
            <ContextualLink
              className="u-margin-L-xs text-gray-dark text-no-decoration"
              onClick={() => updateStudyPlan({ presetPlanCurrentDayNumber: currentDayNumber + 1 })}>
              Next &#x2192;
            </ContextualLink>
          )}
        </div>
      </>
    );
  } else if (isCheckpointsTab) {
    return (
      <CheckpointsTab checkpoints={checkpoints} />
    );
  }
};

export default PresetPlan;
